"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";
import { RemoveScroll } from "react-remove-scroll";

/* ------------------------------------------------------------------ */
/*  Context                                                           */
/* ------------------------------------------------------------------ */

interface OverlayPanelContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentScrollRef: React.RefObject<HTMLDivElement | null>;
}

const OverlayPanelContext =
  React.createContext<OverlayPanelContextValue | null>(null);

function useOverlayPanel() {
  const ctx = React.useContext(OverlayPanelContext);
  if (!ctx)
    throw new Error("useOverlayPanel must be used within <OverlayPanel>.");
  return ctx;
}

/* ------------------------------------------------------------------ */
/*  Root                                                              */
/* ------------------------------------------------------------------ */

interface OverlayPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

function OverlayPanel({ open, onOpenChange, children }: OverlayPanelProps) {
  const contentScrollRef = React.useRef<HTMLDivElement | null>(null);
  const value = React.useMemo(
    () => ({ open, onOpenChange, contentScrollRef }),
    [open, onOpenChange],
  );

  return (
    <OverlayPanelContext.Provider value={value}>
      {children}
      <OverlayPanelBackdrop />
    </OverlayPanelContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Backdrop                                                          */
/* ------------------------------------------------------------------ */

interface OverlayPanelBackdropProps extends React.HTMLAttributes<HTMLDivElement> {}

function OverlayPanelBackdrop({
  className,
  ...props
}: OverlayPanelBackdropProps) {
  const { open, onOpenChange, contentScrollRef } = useOverlayPanel();
  const [mounted, setMounted] = React.useState(false);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 400);
      return () => clearTimeout(timer);
    }
  }, [open]);



  React.useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onOpenChange(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  if (!mounted) return null;

  return createPortal(
    <RemoveScroll as="div" enabled={open} shards={[contentScrollRef]}>
      <div
        data-slot="overlay-panel-backdrop"
        aria-hidden="true"
        onClick={() => onOpenChange(false)}
        className={cn(
          "fixed inset-0 isolate z-30 bg-background/70 supports-backdrop-filter:backdrop-blur-[2px] data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
          "transition-opacity duration-400 ease-[cubic-bezier(0.16,1,0.3,1)]",
          visible ? "opacity-100" : "opacity-0",
          className,
        )}
        {...props}
      />
    </RemoveScroll>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/*  Content                                                           */
/* ------------------------------------------------------------------ */

interface OverlayPanelContentProps extends React.HTMLAttributes<HTMLDivElement> {}

function OverlayPanelContent({
  className,
  children,
  ...props
}: OverlayPanelContentProps) {
  const { open, contentScrollRef } = useOverlayPanel();
  const outerRef = React.useRef<HTMLDivElement>(null);
  const innerRef = React.useRef<HTMLDivElement>(null);

  // Share innerRef with context so RemoveScroll can use it as a shard
  React.useEffect(() => {
    contentScrollRef.current = innerRef.current;
    return () => {
      contentScrollRef.current = null;
    };
  });
  const previousOpenRef = React.useRef(open);
  const [mounted, setMounted] = React.useState(open);

  const getMeasuredHeight = React.useCallback(() => {
    const inner = innerRef.current;
    if (!inner) return 0;
    return inner.getBoundingClientRect().height;
  }, []);

  React.useEffect(() => {
    if (open) {
      setMounted(true);
    }
  }, [open]);

  React.useLayoutEffect(() => {
    const outer = outerRef.current;
    if (!outer || !mounted) return;

    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = open;

    if (open && !wasOpen) {
      outer.style.willChange = "height, opacity, transform";
      outer.style.overflow = "hidden";
      outer.style.transition = "none";
      outer.style.height = "0px";
      outer.style.opacity = "0";
      outer.style.transform = "translateY(-20px)";

      void outer.offsetHeight;

      outer.style.transition = "";
      outer.style.height = `${getMeasuredHeight()}px`;
      outer.style.opacity = "1";
      outer.style.transform = "translateY(0)";
      return;
    }

    if (!open && wasOpen) {
      outer.style.willChange = "height, opacity, transform";
      outer.style.overflow = "hidden";
      outer.style.height = `${getMeasuredHeight()}px`;
      outer.style.opacity = "1";
      outer.style.transform = "translateY(0)";

      void outer.offsetHeight;

      outer.style.height = "0px";
      outer.style.opacity = "0";
      outer.style.transform = "translateY(-20px)";
      return;
    }

    if (open) {
      outer.style.height = "auto";
      outer.style.opacity = "1";
      outer.style.transform = "translateY(0)";
      outer.style.overflow = "";
    }
  }, [getMeasuredHeight, mounted, open]);

  React.useEffect(() => {
    const outer = outerRef.current;
    if (!outer || !mounted) return;

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== outer || event.propertyName !== "height") return;

      outer.style.willChange = "auto";

      if (open) {
        outer.style.height = "auto";
        outer.style.overflow = "";
        return;
      }

      setMounted(false);
    };

    outer.addEventListener("transitionend", handleTransitionEnd);
    return () =>
      outer.removeEventListener("transitionend", handleTransitionEnd);
  }, [mounted, open]);

  React.useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner || !mounted || !open) return;

    const observer = new ResizeObserver(() => {
      if (outer.style.height !== "auto") {
        outer.style.height = `${getMeasuredHeight()}px`;
      }
    });

    observer.observe(inner);
    return () => observer.disconnect();
  }, [getMeasuredHeight, mounted, open]);

  if (!mounted) return null;

  return (
    <div
      ref={outerRef}
      data-slot="overlay-panel-content"
      data-state={open ? "open" : "closed"}
      aria-hidden={!open}
      className={cn(
        "absolute inset-x-0 top-0 z-40 transition-[height,opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
        open ? "pointer-events-auto" : "pointer-events-none",
        className,
      )}
      {...props}
    >
      <div
        ref={innerRef}
        className="max-h-[85dvh] overflow-y-auto overflow-x-hidden [scrollbar-width:none] md:max-h-[85vh]"
      >
        {children}
      </div>
      <div className="container-wrapper pointer-events-none absolute inset-x-0 -bottom-10 z-50">
        <div className="container px-0 flex justify-end">
          <OverlayPanelClose className="pointer-events-auto mr-3" />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Collapse (inverse — collapses when open, expands when closed)     */
/* ------------------------------------------------------------------ */

interface OverlayPanelCollapseProps extends React.HTMLAttributes<HTMLDivElement> {}

function OverlayPanelCollapse({
  className,
  children,
  ...props
}: OverlayPanelCollapseProps) {
  const { open } = useOverlayPanel();
  const innerRef = React.useRef<HTMLDivElement>(null);
  const outerRef = React.useRef<HTMLDivElement>(null);
  const initializedRef = React.useRef(false);

  React.useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    if (!initializedRef.current) {
      outer.style.height = `${inner.scrollHeight}px`;
      initializedRef.current = true;
    }
  }, []);

  React.useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner || !initializedRef.current) return;

    outer.style.willChange = "height, opacity";

    if (open) {
      outer.style.height = `${inner.scrollHeight}px`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          outer.style.height = "0px";
          outer.style.opacity = "0";
          setTimeout(() => {
            if (outer) outer.style.willChange = "auto";
          }, 420);
        });
      });
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          outer.style.height = `${inner.scrollHeight}px`;
          outer.style.opacity = "1";
          setTimeout(() => {
            if (outer) outer.style.willChange = "auto";
          }, 420);
        });
      });
    }
  }, [open]);

  return (
    <div
      ref={outerRef}
      data-slot="overlay-panel-collapse"
      className={cn("overflow-hidden", className)}
      style={{
        opacity: 1,
        transition:
          "height 400ms cubic-bezier(0.16, 1, 0.3, 1), opacity 200ms ease",
      }}
      {...props}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Close Button                                                      */
/* ------------------------------------------------------------------ */

interface OverlayPanelCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

function OverlayPanelClose({
  className,
  children,
  ...props
}: OverlayPanelCloseProps) {
  const { onOpenChange } = useOverlayPanel();

  return (
    <Button
      type="button"
      variant="outline"
      size="icon-sm"
      data-slot="overlay-panel-close"
      onClick={() => onOpenChange(false)}
      className={className}
      {...props}
    >
      {children ?? <X className="" />}
    </Button>
  );
}

/* ------------------------------------------------------------------ */
/*  Exports                                                           */
/* ------------------------------------------------------------------ */

export {
  OverlayPanel,
  OverlayPanelBackdrop,
  OverlayPanelClose,
  OverlayPanelCollapse,
  OverlayPanelContent,
  useOverlayPanel,
};
